import { useState, useEffect } from 'react';
import axios from 'axios';

const API = 'https://weridesudan.com/api/admin';

// ─── Payout Accounts Section ──────────────────────────────────────────────────

function PayoutAccounts({ token }: { token: string }) {
  const [accounts, setAccounts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ accountName: '', accountNumber: '', bankName: '' });
  const [editId, setEditId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<any>({});
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/settings/payout-accounts`, { headers });
      setAccounts(r.data.accounts || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.accountName.trim() || !form.accountNumber.trim() || !form.bankName.trim()) return;
    setSaving(true);
    await axios.post(`${API}/settings/payout-accounts`, form, { headers });
    setForm({ accountName: '', accountNumber: '', bankName: '' });
    await load();
    setSaving(false);
  };

  const toggleActive = async (id: string, current: boolean) => {
    await axios.patch(`${API}/settings/payout-accounts/${id}`, { isActive: !current }, { headers });
    await load();
  };

  const saveEdit = async (id: string) => {
    await axios.patch(`${API}/settings/payout-accounts/${id}`, editForm, { headers });
    setEditId(null);
    await load();
  };

  const remove = async (id: string) => {
    if (!window.confirm('Remove this payout account?')) return;
    await axios.delete(`${API}/settings/payout-accounts/${id}`, { headers });
    await load();
  };

  const inputStyle2: React.CSSProperties = { padding: '8px 12px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 14, width: '100%', boxSizing: 'border-box' };

  return (
    <div style={{ background: '#fff', borderRadius: 12, padding: 20, marginBottom: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
      <h3 style={{ margin: '0 0 4px' }}>🏦 Payout Accounts</h3>
      <p style={{ margin: '0 0 16px', fontSize: 13, color: '#9ca3af' }}>
        Commission bank accounts shown to drivers when locked. Only active accounts are shown.
      </p>

      {/* Add form */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 8, marginBottom: 16 }}>
        <input style={inputStyle2} placeholder="Account name (e.g. Main Account)" value={form.accountName} onChange={e => setForm(p => ({ ...p, accountName: e.target.value }))} />
        <input style={inputStyle2} placeholder="Account number" value={form.accountNumber} onChange={e => setForm(p => ({ ...p, accountNumber: e.target.value }))} />
        <input style={inputStyle2} placeholder="Bank name (e.g. Bankak)" value={form.bankName} onChange={e => setForm(p => ({ ...p, bankName: e.target.value }))} />
        <button
          onClick={add} disabled={saving}
          style={{ padding: '8px 18px', background: '#1a73e8', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' }}
        >
          + Add
        </button>
      </div>

      {/* Accounts list */}
      {loading ? (
        <p style={{ color: '#9ca3af', fontSize: 13 }}>Loading...</p>
      ) : accounts.length === 0 ? (
        <p style={{ color: '#9ca3af', fontSize: 13 }}>No payout accounts yet.</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #f0f0f0' }}>
              <th style={{ textAlign: 'left', padding: '8px 10px', color: '#6b7280', fontWeight: 600 }}>Account Name</th>
              <th style={{ textAlign: 'left', padding: '8px 10px', color: '#6b7280', fontWeight: 600 }}>Account Number</th>
              <th style={{ textAlign: 'left', padding: '8px 10px', color: '#6b7280', fontWeight: 600 }}>Bank</th>
              <th style={{ textAlign: 'left', padding: '8px 10px', color: '#6b7280', fontWeight: 600 }}>Status</th>
              <th style={{ padding: '8px 10px' }}></th>
            </tr>
          </thead>
          <tbody>
            {accounts.map(a => (
              <tr key={a._id} style={{ borderBottom: '1px solid #f9f9f9' }}>
                {editId === a._id ? (
                  <>
                    <td style={{ padding: '6px 10px' }}><input style={inputStyle2} value={editForm.accountName ?? a.accountName} onChange={e => setEditForm((p: any) => ({ ...p, accountName: e.target.value }))} /></td>
                    <td style={{ padding: '6px 10px' }}><input style={inputStyle2} value={editForm.accountNumber ?? a.accountNumber} onChange={e => setEditForm((p: any) => ({ ...p, accountNumber: e.target.value }))} /></td>
                    <td style={{ padding: '6px 10px' }}><input style={inputStyle2} value={editForm.bankName ?? a.bankName} onChange={e => setEditForm((p: any) => ({ ...p, bankName: e.target.value }))} /></td>
                    <td />
                    <td style={{ padding: '6px 10px', display: 'flex', gap: 6 }}>
                      <button onClick={() => saveEdit(a._id)} style={{ padding: '5px 12px', background: '#22c55e', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>Save</button>
                      <button onClick={() => setEditId(null)} style={{ padding: '5px 12px', background: '#9ca3af', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>Cancel</button>
                    </td>
                  </>
                ) : (
                  <>
                    <td style={{ padding: '10px 10px', fontWeight: 500 }}>{a.accountName}</td>
                    <td style={{ padding: '10px 10px', fontFamily: 'monospace', letterSpacing: 1 }}>{a.accountNumber}</td>
                    <td style={{ padding: '10px 10px' }}>{a.bankName}</td>
                    <td style={{ padding: '10px 10px' }}>
                      <span
                        onClick={() => toggleActive(a._id, a.isActive)}
                        style={{ cursor: 'pointer', padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, color: '#fff', background: a.isActive ? '#22c55e' : '#9ca3af', userSelect: 'none' }}
                      >
                        {a.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ padding: '6px 10px', display: 'flex', gap: 6 }}>
                      <button onClick={() => { setEditId(a._id); setEditForm({}); }} style={{ padding: '5px 12px', background: '#1a73e8', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>Edit</button>
                      <button onClick={() => remove(a._id)} style={{ padding: '5px 12px', background: '#e53935', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>Remove</button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function Settings({ token }: { token: string }) {
  // Render PayoutAccounts first (standalone CRUD, not part of the Save Settings form)
  return (
    <div style={{ padding: 24, maxWidth: 900 }}>
      <h2 style={{ marginBottom: 24 }}>⚙️ Settings</h2>
      <PayoutAccounts token={token} />
      <PlatformSettings token={token} />
    </div>
  );
}

function PlatformSettings({ token }: { token: string }) {
  const [settings, setSettings] = useState({
    maintenanceMode: false,
    farePerKm: 10,
    surgeMultiplier: 1.0,
    iosVersion: '1.0.0',
    androidVersion: '1.0.0',
    notificationsEnabled: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/settings`, { headers })
      .then(r => setSettings(r.data))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    await axios.post(`${API}/settings`, settings, { headers });
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  if (loading) return <div style={{ padding: 40, color: '#666' }}>Loading settings...</div>;

  return (
    <div style={{ maxWidth: 600 }}>

      {/* Maintenance Mode */}
      <Section title="🔧 Maintenance Mode" description="When ON, the app will show a maintenance message to all riders and drivers.">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Toggle
            value={settings.maintenanceMode}
            onChange={v => setSettings(s => ({ ...s, maintenanceMode: v }))}
          />
          <span style={{ color: settings.maintenanceMode ? '#ef4444' : '#22c55e', fontWeight: 600 }}>
            {settings.maintenanceMode ? 'Maintenance ON' : 'App is Live'}
          </span>
        </div>
      </Section>

      {/* Fare Settings */}
      <Section title="💰 Fare Settings" description="Set the base fare and surge pricing multiplier.">
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <label style={labelStyle}>Base Fare per KM (SDG)</label>
            <input
              type="number"
              value={settings.farePerKm}
              onChange={e => setSettings(s => ({ ...s, farePerKm: Number(e.target.value) }))}
              style={inputStyle}
              min={1}
            />
          </div>
          <div>
            <label style={labelStyle}>Surge Multiplier</label>
            <input
              type="number"
              value={settings.surgeMultiplier}
              onChange={e => setSettings(s => ({ ...s, surgeMultiplier: Number(e.target.value) }))}
              style={inputStyle}
              min={1} max={5} step={0.1}
            />
          </div>
        </div>
        <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 8 }}>
          Estimated fare for 5km: {(settings.farePerKm * 5 * settings.surgeMultiplier).toFixed(0)} SDG
        </p>
      </Section>

      {/* App Versions */}
      <Section title="📱 App Version Info" description="Track the current live versions of your mobile apps.">
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <label style={labelStyle}>iOS Version</label>
            <input
              value={settings.iosVersion}
              onChange={e => setSettings(s => ({ ...s, iosVersion: e.target.value }))}
              style={inputStyle}
              placeholder="1.0.0"
            />
          </div>
          <div>
            <label style={labelStyle}>Android Version</label>
            <input
              value={settings.androidVersion}
              onChange={e => setSettings(s => ({ ...s, androidVersion: e.target.value }))}
              style={inputStyle}
              placeholder="1.0.0"
            />
          </div>
        </div>
      </Section>

      {/* Notifications */}
      <Section title="🔔 Notifications" description="Enable or disable push notifications to drivers and riders.">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Toggle
            value={settings.notificationsEnabled}
            onChange={v => setSettings(s => ({ ...s, notificationsEnabled: v }))}
          />
          <span style={{ color: settings.notificationsEnabled ? '#22c55e' : '#9ca3af', fontWeight: 600 }}>
            {settings.notificationsEnabled ? 'Notifications Enabled' : 'Notifications Disabled'}
          </span>
        </div>
      </Section>

      {/* Save button */}
      <button
        onClick={save}
        disabled={saving}
        style={{ marginTop: 8, padding: '12px 32px', background: '#1a73e8', color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, cursor: 'pointer' }}
      >
        {saving ? 'Saving...' : '💾 Save Settings'}
      </button>

      {saved && (
        <span style={{ marginLeft: 16, color: '#22c55e', fontWeight: 600 }}>✅ Settings saved!</span>
      )}
    </div>
  );
}

function Section({ title, description, children }: { title: string; description: string; children: React.ReactNode }) {
  return (
    <div style={{ background: '#fff', borderRadius: 12, padding: 20, marginBottom: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
      <h3 style={{ margin: '0 0 4px' }}>{title}</h3>
      <p style={{ margin: '0 0 16px', fontSize: 13, color: '#9ca3af' }}>{description}</p>
      {children}
    </div>
  );
}

function Toggle({ value, onChange }: { value: boolean; onChange: (v: boolean) => void }) {
  return (
    <div
      onClick={() => onChange(!value)}
      style={{
        width: 48, height: 26, borderRadius: 13, cursor: 'pointer',
        background: value ? '#1a73e8' : '#d1d5db',
        position: 'relative', transition: 'background 0.2s',
      }}
    >
      <div style={{
        width: 20, height: 20, borderRadius: '50%', background: '#fff',
        position: 'absolute', top: 3,
        left: value ? 24 : 4,
        transition: 'left 0.2s',
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      }} />
    </div>
  );
}

const labelStyle: React.CSSProperties = { display: 'block', fontSize: 13, color: '#6b7280', marginBottom: 6 };
const inputStyle: React.CSSProperties = { padding: '8px 12px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 14, width: 160 };